/**
 * Performance tab — three sections (ADR-110 / ADR-103 D6/D7), built to the LOCKED
 * mock Docs/Mocks/flow-validator/mock-performance-tab-refactor.html (v9):
 *   1. Telemetry Health — Website → Data Centre  (received-side ground truth)
 *   2. Visitor Performance — Connection Conditions (send-time bands)
 *   3. Visitor Experience (PROPOSED framing kept)  (device mix × page-load)
 *
 * Manual refresh ONLY (no polling — Tinybird budget, ADR-093); a freshness stamp
 * makes the snapshot's age legible (S4d). Layered loaders + graceful degradation
 * on every section (S7). Errors surface as a toast, never inline (toast standard).
 * The benchmark is the single sourced 3s guideline, both devices (ADR-108/110) —
 * it OVERRIDES the split thresholds still shown in the v9 mock §3.
 *
 * ZONES (13 October 2026 — read-only ops boards are zone-agnostic): every section's read fans
 * out to every zone from this browser; each domain row is labelled with its zone; a per-zone
 * legend is always shown; the parent's zone filter (default "All zones") narrows what is shown.
 * Each zone's Tinybird workspace is a separate store, so rows are listed side by side and never
 * summed across zones. A zone that does not respond is named in the legend and a notice.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../../components/ToastProvider';
import {
  getTelemetryHealth, getTelemetryQos, getTelemetryQosTrend, getVisitorExperience,
  type TelemetryHealthRow, type QosBaselineRow, type QosTrendRow, type VisitorExperienceRow,
} from '../../lib/flow-validator-api';
import { StaffApiError } from '../../lib/api-client';
import {
  fanOutZones, failedZoneLabels, throwIfAllZonesFailed, type ZoneResult,
} from '../../lib/zones';
import { ZoneCell, ZoneFanoutWarning } from '../../components/ZoneIndicators';
import { ZoneBoardLegend } from '../../components/ZoneBoardLegend';
import { countInZone, matchesZoneFilter, zoneBoardLegend } from '../../lib/zone-board';
import { FreshnessStamp } from '../../components/telemetry/FreshnessStamp';
import {
  PerfSkeleton, ArrivalRhythm, SpeedGauge, TrendSpark, DeviceMix, LoadBars,
  type RhythmBar, type LoadRow,
} from '../../components/telemetry/PerfPrimitives';

type QosWindow = 24 | 168 | 720;
const QOS_WINDOWS: { key: QosWindow; label: string }[] = [
  { key: 24, label: '24h' }, { key: 168, label: '7d' }, { key: 720, label: '30d' },
];

/** Map a raw StaffApiError/Error to a friendly toast message (never the raw string). */
function friendlyError(err: unknown, what: string): string {
  if (err instanceof StaffApiError) {
    if (err.code === 'request_timeout') return `${what} timed out — please Refresh to try again.`;
    if (err.status === 401) return 'Your session has expired — please sign in again.';
  }
  return `Couldn't load ${what.toLowerCase()} — please Refresh to try again.`;
}

/** Silence verdict → plain-language duration + threshold detail. */
function silenceCaption(row: TelemetryHealthRow): { label: string; cls: string } {
  if (row.health === 'silent') {
    const h = Math.round(row.secondsSinceLastSeen / 3600);
    return { label: h >= 1 ? `Silent ${h} h` : `Silent ${Math.round(row.secondsSinceLastSeen / 60)} min`, cls: 'telp-v-bad' };
  }
  if (row.health === 'unknown') return { label: 'Building', cls: 'telp-v-watch' };
  return { label: 'Receiving', cls: 'telp-v-ok' };
}

function ago(secs: number): string {
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)} min ago`;
  return `${Math.round(secs / 3600)} h ago`;
}

/** One zone's four Performance reads. Trend and visitor-experience degrade to null on their own. */
interface ZonePerf {
  health: TelemetryHealthRow[];
  healthConfigured: boolean;
  qos: QosBaselineRow[];
  trend: QosTrendRow[];
  vx: VisitorExperienceRow[];
}

const PERF_LEGEND_COLUMNS = [
  { key: 'domains', label: 'Domains' },
  { key: 'silent', label: 'Silent' },
] as const;

export function PerformanceTab({ active, zoneFilter }: {
  active: boolean;
  /** The parent's zone filter — `'all'` (the default) or a zone code. Narrows; never targets. */
  zoneFilter: string;
}) {
  const toast = useToast();

  const [results, setResults] = useState<ZoneResult<ZonePerf>[] | null>(null);
  const [allHealth, setHealth] = useState<TelemetryHealthRow[]>([]);
  const [healthConfigured, setHealthConfigured] = useState(true);
  const [allQos, setQos] = useState<QosBaselineRow[]>([]);
  const [qosTrend, setQosTrend] = useState<Map<string, QosTrendRow[]>>(new Map());
  const [allVx, setVx] = useState<VisitorExperienceRow[]>([]);
  const [qosWindow, setQosWindow] = useState<QosWindow>(24);

  const [loading, setLoading] = useState(false);
  const [loadedAt, setLoadedAt] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);

  const loadAll = useCallback(async (win: QosWindow) => {
    setLoading(true);
    try {
      // One consolidated load — all three sections fetched in parallel (S7), in every zone at
      // once (one request set per zone, from this browser — ADR-120 Amendment 2).
      const res = await fanOutZones(async (zone): Promise<ZonePerf[]> => {
        const [h, q, t, x] = await Promise.all([
          getTelemetryHealth(undefined, zone),
          getTelemetryQos(win, zone),
          getTelemetryQosTrend(win, zone).catch(() => null),
          getVisitorExperience(undefined, zone).catch(() => null),
        ]);
        return [{ health: h.rows, healthConfigured: h.configured, qos: q.rows, trend: t?.rows ?? [], vx: x?.rows ?? [] }];
      });
      setResults(res);
      throwIfAllZonesFailed(res);
      const tag = <T extends object>(pick: (p: ZonePerf) => T[]) =>
        res.flatMap((r) => (r.items[0] ? pick(r.items[0]).map((row) => ({ ...row, zone: r.zone.code })) : []));
      setHealth(tag((p) => p.health));
      setHealthConfigured(res.some((r) => r.items[0]?.healthConfigured));
      setQos(tag((p) => p.qos));
      // Keyed by zone AND domain — a trend is one zone's series, never mixed with another's.
      const byDomain = new Map<string, QosTrendRow[]>();
      for (const row of tag((p) => p.trend)) {
        const k = `${row.zone}|${row.domainToken}`;
        const arr = byDomain.get(k) ?? [];
        arr.push(row);
        byDomain.set(k, arr);
      }
      setQosTrend(byDomain);
      setVx(tag((p) => p.vx));
      setLoadedAt(Date.now());
      setLoaded(true);
      const failed = failedZoneLabels(res);
      if (failed.length) {
        toast.push({
          variant: 'error',
          title: 'Some performance data could not be loaded',
          detail: `${failed.join(' and ')} did not respond — its domains are missing from these sections.`,
        });
      }
    } catch (err) {
      toast.push({ variant: 'error', ...toastBody(friendlyError(err, 'Performance data')) });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Lazy first load + reload on the Visitor-Performance window change.
  useEffect(() => {
    if (active) void loadAll(qosWindow);
  }, [active, qosWindow, loadAll]);

  // Per-zone legend over the UNFILTERED rows; the sections below show the zone-filtered rows.
  const legend = useMemo(() => zoneBoardLegend(results, {
    counts: (code) => ({
      domains: countInZone(allHealth, code),
      silent: countInZone(allHealth, code, (r) => r.health === 'silent'),
    }),
  }), [results, allHealth]);
  const health = useMemo(() => allHealth.filter((r) => matchesZoneFilter(r, zoneFilter)), [allHealth, zoneFilter]);
  const qos = useMemo(() => allQos.filter((r) => matchesZoneFilter(r, zoneFilter)), [allQos, zoneFilter]);
  const vx = useMemo(() => allVx.filter((r) => matchesZoneFilter(r, zoneFilter)), [allVx, zoneFilter]);

  const silentCount = useMemo(() => health.filter((r) => r.health === 'silent').length, [health]);
  const receivedTotal = useMemo(() => health.reduce((s, r) => s + r.beaconsInWindow, 0), [health]);

  // Group visitor-experience rows by domain for the device mix + load bars.
  const vxByDomain = useMemo(() => groupVx(vx), [vx]);

  if (!loaded && loading) {
    return (
      <div className="telp-section">
        <ZoneBoardLegend
          rows={legend}
          columns={PERF_LEGEND_COLUMNS}
          testId="fv-perf-zone-legend"
          ariaLabel="Performance domains by zone"
          loading
        />
        <PerfSkeleton label="Loading Telemetry Health from every zone…" rows={2} />
        <PerfSkeleton label="Loading Visitor Performance from every zone…" rows={2} />
      </div>
    );
  }

  return (
    <div className="telp-section">
      {/* Manual-refresh control row — measured to one height (standing directive). */}
      <div className="telp-controls">
        <button type="button" className="telp-refresh" onClick={() => void loadAll(qosWindow)} disabled={loading}>
          {loading ? 'Loading…' : '↻ Refresh'}
        </button>
        <FreshnessStamp at={loadedAt} />
      </div>

      {/* Per-zone legend — always visible, whatever the zone filter shows. */}
      <ZoneBoardLegend
        rows={legend}
        columns={PERF_LEGEND_COLUMNS}
        testId="fv-perf-zone-legend"
        ariaLabel="Performance domains by zone"
        loading={loading && !results}
      />
      <ZoneFanoutWarning
        failedZones={results ? failedZoneLabels(results) : []}
        noun="performance domains"
        onRetry={() => void loadAll(qosWindow)}
      />

      {/* ============ SECTION 1 · TELEMETRY HEALTH ============ */}
      <div className="telp-card">
        <div className="telp-hd">
          <h3>Telemetry Health — Website → Data Centre</h3>
          {silentCount > 0
            ? <span className="telp-badge warn">{silentCount} Of {health.length} Domains Silent</span>
            : <span className="telp-badge ok">All Domains Receiving</span>}
        </div>
        <div className="telp-what">
          <b>Is telemetry flowing?</b> Beacons <b>received</b> per website (edge-side ground truth),
          each domain's arrival rhythm, and silence judged against its own rhythm. This is the platform's snippet-down detection.
        </div>
        {/* Pipeline strip — received counts are ground truth; DLQ/probe come from
            the platform monitors (ADR-104), not fabricated here. */}
        <div className="telp-pipe">
          <span className="telp-node"><b>Received (7d)</b><span>{receivedTotal}</span></span>
          <span className="telp-arr">→</span>
          <span className="telp-node"><b>Queue</b><span>{receivedTotal}</span></span>
          <span className="telp-arr">→</span>
          <span className="telp-node"><b>Tinybird</b><span>{receivedTotal}</span></span>
          <span className="telp-pipe-meta">edge → queue → Tinybird; DLQ &amp; synthetic-probe are reported by the platform monitors</span>
        </div>

        {!healthConfigured && <div className="telp-empty">Telemetry health is not configured (Tinybird token missing).</div>}
        {healthConfigured && health.length === 0 && <div className="telp-empty">No domains have sent beacons in the window.</div>}

        {health.map((row) => {
          const v = silenceCaption(row);
          const bars: RhythmBar[] = row.arrivals ?? [];
          const silentH = row.health === 'silent' ? Math.round(row.secondsSinceLastSeen / 3600) : null;
          return (
            <div className={`telp-row telh${row.health === 'silent' ? ' telp-silent' : ''}`} key={`${row.zone}|${row.domainToken}`} data-zone={row.zone}>
              <span className="telp-dn">
                <b>{row.hostname || 'unknown'}</b><span>{row.domainToken}</span><br />
                <span className="telp-zone" data-testid="fv-zone-cell"><ZoneCell zone={row.zone} /></span><br />
                <span className={`telp-verdict ${v.cls}`}>{v.label}</span>
              </span>
              <span className="telp-blk telp-rhythm">
                <span className="telp-t">Arrival Rhythm — Beacons Per Hour (48h)</span>
                <ArrivalRhythm bars={bars} silentHours={silentH} />
                <span className="telp-cap">
                  {row.health === 'silent'
                    ? <>rhythm stopped · last beacon <b className="bad">{ago(row.secondsSinceLastSeen)}</b></>
                    : row.lastSeen
                      ? <>last beacon <b className="good">{ago(row.secondsSinceLastSeen)}</b> · typical gap ~{Math.round(row.typicalGapSeconds / 60)} min</>
                      : <>no beacons yet</>}
                </span>
              </span>
              <span className="telp-blk">
                <span className="telp-t">Received (window)</span>
                <span className="telp-bignum">{row.beaconsInWindow} <span className="telp-unit">beacons</span></span>
                {row.health === 'silent' && <span className="telp-s">snippet removed in a site rebuild · site down · script blocked</span>}
              </span>
              {row.health === 'silent' && (
                <span className="telp-alertline">⚠ Alert raised on the Alerts board (snippet source) — silence detection productises the stored-but-never-checked lastSeen</span>
              )}
            </div>
          );
        })}

        <div className="telp-key">
          <b>Key</b> — <b>Receiving</b> = beacons arriving within the domain's own rhythm ·
          <b>Silent</b> = no beacons for ≫ its own typical gap (N× threshold) → alert ·
          <b>Building</b> = too few beacons to judge a rhythm yet (fails open — never called silent) ·
          rhythm chart = beacons received per hour at OUR edge; silence is visible as the bars stopping.
        </div>
      </div>

      {/* ============ SECTION 2 · VISITOR PERFORMANCE ============ */}
      <div className="telp-card">
        <div className="telp-hd">
          <h3>Visitor Performance — Connection Conditions</h3>
          <span className="telp-badge ok">{qos.length} Domain{qos.length === 1 ? '' : 's'} Reporting</span>
        </div>
        <div className="telp-what">
          <b>What conditions do this site's visitors browse under?</b> The spread of times profiles
          <b> the connections visitors are on while browsing the subscriber's website</b> — their devices, networks and locations.
          It is not the web host, not Nexa, and not pipeline health (that's Telemetry Health above).
          <span className="telp-mech"> Mechanism: the snippet times its own telemetry sends; a send is a whole job (DNS + secure connection + request + response).</span>
        </div>
        <div className="telp-controls">
          <div className="telp-win" role="group" aria-label="Window">
            {QOS_WINDOWS.map((w) => (
              <button key={w.key} type="button" className={qosWindow === w.key ? 'on' : ''} onClick={() => setQosWindow(w.key)} disabled={loading}>{w.label}</button>
            ))}
          </div>
        </div>

        {qos.length === 0 && <div className="telp-empty">No connection-conditions data in this window.</div>}
        {qos.map((row) => {
          const bands = { fast: row.bandFast, ok: row.bandOk, slow: row.bandSlow, verySlow: row.bandVerySlow };
          const total = bands.fast + bands.ok + bands.slow + bands.verySlow;
          const good = row.rttP50 < 500;
          const trend = qosTrend.get(`${row.zone}|${row.domainToken}`) ?? [];
          const trendPts = trend.map((t) => t.rttP50);
          const worsening = trendPts.length >= 2 && trendPts[trendPts.length - 1] > trendPts[0];
          return (
            <div className="telp-row telp-vp" key={`${row.zone}|${row.domainToken}`} data-zone={row.zone}>
              <span className="telp-dn">
                <b>{row.hostname || 'unknown'}</b><span>{row.domainToken}</span><br />
                <span className="telp-zone" data-testid="fv-zone-cell"><ZoneCell zone={row.zone} /></span><br />
                <span className={`telp-verdict ${good ? 'telp-v-ok' : 'telp-v-watch'}`}>{good ? 'Good Conditions' : 'Slower Conditions'}</span>
              </span>
              <span className="telp-blk">
                <span className="telp-gtitle">
                  Send Time — typical <b>{fmtMs(row.rttP50)}</b> · slowest <b>{fmtMs(row.rttP95)}</b> · {row.samples} sends
                </span>
                <SpeedGauge bands={bands} typicalMs={row.rttP50} />
                {trendPts.length >= 2 && (
                  <span className="telp-strend">
                    <TrendSpark points={trendPts} color={worsening ? '#fdba74' : '#34d399'} />
                    <span>Conditions over 7 days: <b className={worsening ? 'down' : 'up'}>{worsening ? 'worsening' : 'improving'}</b> — the mix of connections visitors arrive on</span>
                  </span>
                )}
                {total === 0 && <span className="telp-s">send-time bands build once the gauge data pipe is deployed</span>}
              </span>
            </div>
          );
        })}

        <div className="telp-key">
          <b>Key</b> — speed bands: <span className="telp-sw" style={{ background: '#0d3b2e' }} />fast &lt;100 ms ·
          <span className="telp-sw" style={{ background: '#14532d' }} />ok &lt;500 ms ·
          <span className="telp-sw" style={{ background: '#713f12' }} />slow &lt;2 s ·
          <span className="telp-sw" style={{ background: '#7f1d1d' }} />very slow &gt;2 s ·
          <b> numbered circles = sends per band</b> · <b style={{ color: '#6ee7b7' }}>▼ typical</b> = the middle send ·
          no reliability figure here — arrival/failure is Telemetry Health's job.
        </div>
      </div>

      {/* ============ SECTION 3 · VISITOR EXPERIENCE (PROPOSED) ============ */}
      <div className="telp-card telp-prop">
        <div className="telp-hd">
          <h3>Visitor Experience<span className="telp-prop-tag">PROPOSED</span></h3>
          <span className="telp-badge ok">{vxByDomain.length} Domain{vxByDomain.length === 1 ? '' : 's'} Reporting</span>
        </div>
        <div className="telp-what">
          <b>Who visits, and how fast the subscriber's own site loads for them.</b> Device mix is full-history;
          load-time collection builds from launch. Feeds the subscriber-side Experience modal in the Website Section.
        </div>

        {vxByDomain.length === 0 && <div className="telp-empty">No visitor-experience samples yet — nav-timing accrues from the taxonomy deploy forward.</div>}
        {vxByDomain.map((d) => (
          <div className="telp-row telp-vx" key={`${d.zone}|${d.domainToken}`} data-zone={d.zone}>
            <span className="telp-dn">
              <b>{d.hostname || 'unknown'}</b><span>{d.domainToken}</span><br />
              <span className="telp-zone" data-testid="fv-zone-cell"><ZoneCell zone={d.zone} /></span><br />
              <span className={`telp-verdict ${d.mobileHeavySlow ? 'telp-v-watch' : 'telp-v-ok'}`}>
                {d.mobileHeavySlow ? 'Mobile-Heavy · Slow On Mobile' : 'Balanced'}
              </span>
            </span>
            <span className="telp-blk">
              <span className="telp-t">Who Visits — Device Mix (Full History)</span>
              <DeviceMix share={d.share} />
            </span>
            <span className="telp-blk">
              <span className="telp-t">Their Site's Page-Load, As Visitors Experience It</span>
              {d.loadRows.length > 0
                ? <><LoadBars rows={d.loadRows} /><span className="telp-s">the white line marks 3 s — a guideline from Google mobile page-speed research, not a pass/fail</span></>
                : <span className="telp-s">speed history builds from launch — low sample</span>}
            </span>
          </div>
        ))}
        <div className="telp-modalnote">This panel also ships as a CJI Scorecard (Website Section) modal with a launcher button — subscriber copy, no domain tokens.</div>
      </div>
    </div>
  );
}

// ── helpers ──
function fmtMs(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms)} ms`;
}
function toastBody(message: string): { title: string; detail: string } {
  return { title: 'Telemetry', detail: message };
}

interface VxDomain {
  zone?: string;
  domainToken: string;
  hostname: string;
  share: { mobile: number; desktop: number; tablet: number };
  loadRows: LoadRow[];
  mobileHeavySlow: boolean;
}
function groupVx(rows: VisitorExperienceRow[]): VxDomain[] {
  const byDomain = new Map<string, VisitorExperienceRow[]>();
  // Grouped per zone AND domain — a device mix is one zone's figure, never pooled across zones.
  for (const r of rows) {
    const k = `${r.zone ?? ''}|${r.domainToken}`;
    const arr = byDomain.get(k) ?? [];
    arr.push(r);
    byDomain.set(k, arr);
  }
  const out: VxDomain[] = [];
  for (const list of byDomain.values()) {
    const domainToken = list[0]!.domainToken;
    const zone = list[0]!.zone;
    const hostname = list[0]?.hostname ?? '';
    const total = list.reduce((s, r) => s + r.samples, 0) || 1;
    const dev = (t: string) => list.find((r) => r.deviceType === t);
    const pct = (t: string) => Math.round(((dev(t)?.samples ?? 0) / total) * 100);
    const share = { mobile: pct('mobile'), desktop: pct('desktop'), tablet: pct('tablet') };
    const loadRows: LoadRow[] = (['mobile', 'desktop', 'tablet'] as const)
      .map((t) => dev(t))
      .filter((r): r is VisitorExperienceRow => !!r && r.typicalLoadMs > 0)
      .map((r) => ({ device: r.deviceType.charAt(0).toUpperCase() + r.deviceType.slice(1), typicalMs: r.typicalLoadMs, samples: r.samples }));
    const mobile = dev('mobile');
    const mobileHeavySlow = share.mobile >= 55 && !!mobile && mobile.typicalLoadMs > 3000;
    out.push({ zone, domainToken, hostname, share, loadRows, mobileHeavySlow });
  }
  return out.sort((a, b) => (b.share.mobile - a.share.mobile));
}
